import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader, KpiCard } from "@cx/ui";
import type { ProductInsightRow, ProductInsightsResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatDateBR, type CoverageBand } from "@cx/domain";
import { useSession } from "../../session";
import { count, money, qty, reportQuery } from "../dashboard/labels";
import { FilterRow, ReportStatus, reportQueryOptions } from "../dashboard/shared";
import { ExportButtons } from "./shared";

const PATH = "/meu-negocio/produtos";
export const BAND_LABEL: Record<CoverageBand, string> = {
  rupture: "Ruptura",
  d7: "Até 7 dias",
  d30: "Até 30 dias",
  over: "Mais de 30 dias",
  no_sales: "Sem giro",
};
const BAND_TONE: Record<CoverageBand, string> = {
  rupture: "danger",
  d7: "warning",
  d30: "info",
  over: "success",
  no_sales: "secondary",
};

/**
 * Produtos (RF-BI-10): previsão de estoque (cobertura em dias), valor imobilizado (com custo), sem movimentação há N
 * dias e atalho para a sugestão de compras do Planejador (T3.7). Estado atual do estoque.
 */
export function ProductsBiPage(): ReactElement {
  const { companyId, api, me } = useSession();
  const perms = me.permissions as Permissions;
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const params = { salesDays: search.salesDays?.toString(), idleDays: search.idleDays?.toString() };
  const q = useQuery({
    ...reportQueryOptions,
    queryKey: ["analytics", companyId, "products", params],
    queryFn: () => api<ProductInsightsResult>(`/analytics/products${reportQuery(params)}`),
  });
  const r = q.data;
  const withCost = r?.stockValueCents !== undefined;
  return (
    <>
      <ContentHeader
        title="Produtos e estoque"
        breadcrumb={["Meu Negócio", "Produtos e estoque"]}
        actions={<ExportButtons report="products" params={params} />}
      />
      <FilterRow>
        <div className="col-6 col-md-3">
          <label className="form-label small" htmlFor="bi-sales-days">
            Média de venda dos últimos (dias)
          </label>
          <input
            id="bi-sales-days"
            type="number"
            min={7}
            max={365}
            className="form-control"
            value={search.salesDays ?? 90}
            onChange={(e) => set({ salesDays: Number(e.target.value) || undefined })}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small" htmlFor="bi-idle-days">
            Sem movimentação há (dias)
          </label>
          <input
            id="bi-idle-days"
            type="number"
            min={7}
            max={730}
            className="form-control"
            value={search.idleDays ?? 90}
            onChange={(e) => set({ idleDays: Number(e.target.value) || undefined })}
          />
        </div>
        {can(perms, "ai_agents", "read") && (
          <div className="col-12 col-md-6 text-md-end">
            <Link to="/meu-negocio/agentes" className="btn btn-outline-primary">
              Sugestão de compras (Planejador)
            </Link>
          </div>
        )}
      </FilterRow>
      <ReportStatus loading={q.isLoading} error={q.error} />
      {r && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-xl-3">
              <KpiCard
                label="Produtos com estoque controlado"
                value={count(r.products)}
                testId="bi-products"
              />
            </div>
            <div className="col-6 col-xl-3">
              <KpiCard
                label="Em ruptura ou até 7 dias"
                value={count(r.bands.rupture + r.bands.d7)}
                hint={`${count(r.bands.d30)} com até 30 dias · ${count(r.bands.no_sales)} sem giro`}
                testId="bi-coverage"
              />
            </div>
            <div className="col-6 col-xl-3">
              <KpiCard
                label={`Sem movimentação há ${r.idleDays} dias`}
                value={count(r.idleCount)}
                hint={withCost ? `Valor parado: ${money(r.idleValueCents ?? null)}` : undefined}
                testId="bi-idle"
              />
            </div>
            {withCost && (
              <div className="col-6 col-xl-3">
                <KpiCard
                  label="Valor imobilizado"
                  value={money(r.stockValueCents ?? null)}
                  testId="bi-value"
                />
              </div>
            )}
          </div>
          {r.truncated && (
            <div className="alert alert-warning">
              Mostrando os 5.000 primeiros produtos com estoque controlado.
            </div>
          )}
          <ProductTable
            title={`Previsão de estoque (média dos últimos ${r.salesDays} dias)`}
            rows={r.coverage}
            withCost={withCost}
            empty="Nenhum produto em ruptura ou com até 30 dias de cobertura."
            testId="bi-coverage-table"
          />
          <ProductTable
            title={`Sem movimentação há ${r.idleDays} dias`}
            rows={r.idle}
            withCost={withCost}
            empty="Nenhum produto parado."
            testId="bi-idle-table"
          />
          {r.value && (
            <ProductTable
              title="Maior valor imobilizado"
              rows={r.value}
              withCost
              empty="Nenhum produto com saldo e custo."
              testId="bi-value-table"
            />
          )}
        </>
      )}
    </>
  );
}

function ProductTable({
  title,
  rows,
  withCost,
  empty,
  testId,
}: {
  title: string;
  rows: ProductInsightRow[];
  withCost: boolean;
  empty: string;
  testId: string;
}): ReactElement {
  return (
    <section className="box card mb-3" aria-label={title} data-testid={testId}>
      <div className="card-header">
        <h2 className="h6 mb-0">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted p-3 mb-0">{empty}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label={title}>
            <thead>
              <tr>
                <th scope="col">Produto</th>
                <th scope="col" className="text-end">
                  Disponível
                </th>
                <th scope="col" className="text-end">
                  A receber
                </th>
                <th scope="col" className="text-end">
                  Média diária
                </th>
                <th scope="col" className="text-end">
                  Cobertura
                </th>
                <th scope="col">Último movimento</th>
                {withCost && (
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.productId}>
                  <td>
                    {p.name}
                    {p.sku && <span className="text-muted small ms-1">({p.sku})</span>}
                  </td>
                  <td className="text-end">{qty(p.availableQ4)}</td>
                  <td className="text-end">{qty(p.onOrderQ4)}</td>
                  <td className="text-end">{qty(p.avgDailyQ4)}</td>
                  <td className="text-end">
                    <span className={`badge text-bg-${BAND_TONE[p.band]}`}>
                      {p.coverageDays === null ? BAND_LABEL[p.band] : `${count(p.coverageDays)} dia(s)`}
                    </span>
                  </td>
                  <td>{p.lastMovementDay ? formatDateBR(p.lastMovementDay) : "—"}</td>
                  {withCost && <td className="text-end">{money(p.valueCents ?? null)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
